import { buildAnalysisPrompt } from "./promptSelector";
import { normalizeFinancialPayload } from "./normalizeFinancialPayload";
import { buildPromptReportContext } from "./reportContextBuilder";

const getFinancialData = (apiResponse, reportData) => {
  if (apiResponse?.data) return { financialData: apiResponse.data, dataSource: "originalApiResponse.data" };
  if (apiResponse?.rows || apiResponse?.columns) return { financialData: apiResponse, dataSource: "originalApiResponse" };
  if (apiResponse && typeof apiResponse === "object") {
    return { financialData: apiResponse, dataSource: "originalApiResponse.object" };
  }
  if (reportData?.originalData?.data) {
    return { financialData: reportData.originalData.data, dataSource: "reportData.originalData.data" };
  }
  return { financialData: reportData?.originalData || reportData || null, dataSource: "reportData.fallback" };
};

const pickFirstValue = (source = {}, keys = []) =>
  keys.map((key) => source?.[key]).find((value) => value !== undefined && value !== null && value !== "");

const getStoredUser = () => {
  if (typeof window === "undefined") return null;

  try {
    const rawUser = window.localStorage?.getItem("user");
    return rawUser ? JSON.parse(rawUser) : null;
  } catch {
    return null;
  }
};

const mergeUserSources = (user) => ({
  ...(getStoredUser() || {}),
  ...(user || {}),
});

const normalizeContextValue = (value, objectKeys = []) => {
  if (!value) return null;
  if (typeof value === "string") return value.trim() || null;
  if (Array.isArray(value)) {
    return value
      .map((item) => normalizeContextValue(item, objectKeys))
      .filter(Boolean)
      .join(", ") || null;
  }
  if (typeof value === "object") {
    return pickFirstValue(value, objectKeys) || null;
  }
  return String(value);
};

const buildCustomerContext = (user) => {
  const resolvedUser = mergeUserSources(user);

  const customerContext = {
    name: pickFirstValue(resolvedUser, ["Name", "FullName", "CustomerName", "UserName", "Username"]) || null,
    niche: normalizeContextValue(
      pickFirstValue(resolvedUser, ["Nitch", "Niche", "BusinessType", "Industry", "Category"]),
      ["NitchName", "Name", "label", "title", "value"]
    ),
    brief: pickFirstValue(resolvedUser, ["Note", "Brief", "CustomerBrief", "Description"]) || null,
    interest: normalizeContextValue(
      pickFirstValue(resolvedUser, ["Interest", "Interests", "Sector", "Specialty", "FocusArea"]),
      ["InterestName", "Name", "label", "title", "value"]
    ),
  };

  Object.keys(customerContext).forEach((key) => {
    if (customerContext[key] === null) {
      delete customerContext[key];
    }
  });

  return customerContext;
};

const resolveEffectivePeriod = (filters, financialData) =>
  filters?.period ||
  financialData?.overview?.date ||
  financialData?.balanceSheet?.overview?.date ||
  (filters?.periodFrom && filters?.periodTo ? `${filters.periodFrom} -> ${filters.periodTo}` : null) ||
  filters?.periodFrom ||
  filters?.periodTo ||
  null;

const buildPromptFilters = (filters, excelData) => ({
  currency: filters?.currency || null,
  period: filters?.period || null,
  periodFrom: filters?.periodFrom || null,
  periodTo: filters?.periodTo || null,
  groupBy: filters?.groupBy || null,
  analysisType: filters?.analysisType || null,
  dataType: filters?.dataType || null,
  comparePeriodUnit: filters?.compare_period_unit || null,
  periodCount: filters?.period_count || null,
  hasUploadedExcel: Boolean(excelData),
});

export const prepareDataForChatGPT = (
  reportData,
  filters,
  reportType,
  originalApiResponse,
  excelData = null,
  language = "Arabic",
  allowedModels = null,
  user = null
) => {
  const apiResponse = originalApiResponse || reportData?.originalData || reportData;
  const { financialData, dataSource } = getFinancialData(apiResponse, reportData);

  if (!financialData) {
    throw new Error("No financial data available for analysis");
  }

  if (financialData.isMockData) {
    throw new Error("Mock report data cannot be sent for AI analysis");
  }

  const effectivePeriod = resolveEffectivePeriod(filters, financialData);
  const customerContext = buildCustomerContext(user);
  const normalizedFinancialData = normalizeFinancialPayload({
    filters,
    financialData,
    reportType,
  });
  const reportContext = buildPromptReportContext({
    customerContext,
    dataSource,
    effectivePeriod,
    filters,
    financialData: normalizedFinancialData,
    language,
    reportType,
  });

  const validation = {
    isValid: true,
    hasData: Boolean(financialData),
    promptContractVersion: "json-v1",
    reportType,
    period: effectivePeriod,
    currency: filters?.currency || null,
    dataSource,
    hasExcelData: Boolean(excelData),
    reportKind: normalizedFinancialData.reportKind,
    customerContext,
  };

  const promptFilters = buildPromptFilters(filters, excelData);

  const enhancedData = {
    metadata: {
      reportType,
      period: effectivePeriod,
      currency: filters?.currency || null,
      language,
      dataSource,
      analysisDate: new Date().toISOString(),
      // `AssistantId` لم يعد يصل من الخادم: م3.2 أخرج المفتاح ومعرّف الـ Assistant
      // من ردّ /client/reports، وAssistants API نفسه يُزال في 26/8/2026.
      // البديل هوية النموذج الحقيقية، وهي ما يفيد فعلاً عند تشخيص شكوى.
      modelId: allowedModels?.Id ?? null,
      modelName: allowedModels?.Name || "",
      provider: allowedModels?.Provider || "",
      reportKind: normalizedFinancialData.reportKind,
    },
    customerContext,
    reportContext,
    normalizedFinancialData,
    filters: promptFilters,
    excelData: excelData || null,
  };

  return {
    prompt: buildAnalysisPrompt({
      customerContext,
      excelData,
      filters: promptFilters,
      normalizedFinancialData,
      reportContext,
      language,
    }),
    enhancedData,
    validation,
  };
};
